import {
  APP_INITIALIZER, Injectable, InjectionToken, Injector, OnDestroy, PLATFORM_ID, Provider, inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription, combineLatest } from 'rxjs';

import { ActiveStore, StoreContextService } from '../services/store-context.service';
import { StorageKey, StorageService } from '../services/storage.service';
import { HubDeviceService, HubRoleSnapshot } from './hub-device.service';
import { HubDiscoveryService } from './hub-discovery.service';
import { isHubCapablePath } from './hub-surfaces';

/**
 * OFP-082 — does a page loaded at `pathname` start hub discovery at boot?
 *
 * Only on a POS operator route: `/pos` or under it, minus the Payment
 * Terminal (core/offline/hub-surfaces). Login, store selection, device
 * registration and tenant routes never needed discovery, and with a cached
 * offline store and a hub backlog the drain hold would send their calls to
 * the hub (OFP-082 review). A Payment Terminal device is refused separately
 * (HubBootstrapService.start), because it cold-boots through `/pos/s/<code>`
 * like a POS before its guard moves it to `/pos/s/<code>/pt`.
 */
export function hubDiscoveryAppliesTo(pathname: string | null | undefined): boolean {
  return isHubCapablePath(pathname);
}

/**
 * The path the page was loaded at, read by HUB_DISCOVERY_INITIALIZER before
 * the router has navigated. A token so a spec can say which page it is
 * reloading (the test runner's own page is not a POS route).
 */
export const HUB_BOOT_PATHNAME = new InjectionToken<() => string>('HUB_BOOT_PATHNAME', {
  providedIn: 'root',
  factory: () => () => (typeof window !== 'undefined' ? window.location.pathname : ''),
});

/** The store write that goes with a target: seed the shell's hub, or drop the seed. */
export type HubSeed = { kind: 'seed'; url: string } | { kind: 'clear' };

/** Which LAN hub URL a terminal probes, and where that URL came from. */
export interface HubTarget {
  url: string | null;
  source: 'store' | 'shell' | null;
  seed?: HubSeed;
}

const NO_HUB: HubTarget = { url: null, source: null };

const sameStoreId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a?.trim() && !!b?.trim() && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * OFP-082 — the LAN hub a terminal should probe, from its cached store and
 * the edge shell's hub-role snapshot, and whether to write the shell's hub
 * onto the store. Pure, so the precedence is testable.
 *
 * - The store's own offline settings win whenever they have loaded: its hub
 *   URL while offline is on, no hub when offline is off or no URL is set.
 *   They are what the interceptor routes with, and discovery must probe the
 *   hub the requests actually go to.
 * - While they have NOT loaded (`offlineEnabled` null), or the store holds
 *   only an earlier seed (`offlineHubFromShell`), the shell's snapshot
 *   bootstraps them — but only a snapshot that names THIS store
 *   (`hub:role.storeId`, edge #44). The shell polls the store's
 *   OfflineHubUrl / OfflineEnabled from the cloud and keeps them across
 *   restarts, so it knows the hub on a reload where this page never got as
 *   far as loading store settings. With no storeId (older edges), or
 *   another store's, nothing is seeded and discovery waits for the store's
 *   settings, as before (OFP-082 review: the snapshot otherwise has no
 *   store identity, and admin flows set the active store too).
 * - A seed keeps following the shell — on later boots as well — until real
 *   settings replace it (the settings writers clear the marker). It is
 *   dropped only when the snapshot names ANOTHER store, or says this
 *   store's offline mode is off, or has no hub for it. Before the shell
 *   answers, or while its snapshot names no store (storeId null), the seed
 *   stands and nothing is written (OFP-082 re-review: a role cached by an
 *   older edge build carries storeId null until its first successful poll,
 *   and clearing on it locked a hub restarted mid-outage out of PIN login).
 * - No store at all: nothing. The hub device does not need this — it
 *   probes its own loopback URL whatever this returns.
 */
export function resolveHubTarget(
  store: ActiveStore | null | undefined,
  snap: HubRoleSnapshot | null | undefined,
): HubTarget {
  if (!store) return NO_HUB;
  const seeded = store.offlineHubFromShell === true;
  const storeUrl = store.offlineHubUrl?.trim() || null;
  if (!seeded) {
    if (store.offlineEnabled === true) return storeUrl ? { url: storeUrl, source: 'store' } : NO_HUB;
    if (store.offlineEnabled === false) return NO_HUB;
  }
  // No snapshot, or one naming no store: no claim either way.
  const shellStore = snap?.storeId?.trim() || null;
  if (!snap || !shellStore) {
    return seeded && storeUrl ? { url: storeUrl, source: 'shell' } : NO_HUB;
  }
  // From here the snapshot speaks for a store — this one, or another.
  const shellUrl = sameStoreId(shellStore, store.storeId) && snap.offlineEnabled === true
    ? (snap.hubUrl?.trim() || null)
    : null;
  if (shellUrl) {
    const upToDate = seeded && store.offlineEnabled === true && storeUrl === shellUrl;
    return { url: shellUrl, source: 'shell', seed: upToDate ? undefined : { kind: 'seed', url: shellUrl } };
  }
  return seeded ? { ...NO_HUB, seed: { kind: 'clear' } } : NO_HUB;
}

/**
 * OFP-082 — the single owner of `HubDiscoveryService.start()`. Starts hub
 * discovery and keeps it pointed at the right hub for the page's life.
 *
 * Discovery used to start only from the POS shell's store-settings reply.
 * The order, settle, booking and sync-issues screens are sibling routes
 * where the shell never mounts, so a satellite reloaded on one of them
 * never probed its hub: during an outage `decide()` said 'block', the
 * banner read "Cannot reach store hub", hub realtime stayed off, and
 * online the RECON-4 drain hold was silently disabled. Now the
 * APP_INITIALIZER below starts this from the store hydrated out of local
 * storage, before any screen renders, and it re-targets whenever the store
 * or the shell's hub role changes. The POS shell only writes the settings
 * it loads into the store (and calls `start()`, for a page that booted on a
 * route outside the allow-list): two owners disagreed (OFP-082 review —
 * settings with `offlineEnabled` null stopped the probe the shell's hub
 * had started, and the terminal ended on 'block').
 */
@Injectable({ providedIn: 'root' })
export class HubBootstrapService implements OnDestroy {
  private sub: Subscription | null = null;
  /** URL last handed to discovery; `undefined` until the first evaluation. */
  private lastUrl: string | null | undefined = undefined;

  constructor(
    private storeCtx: StoreContextService,
    private hubDevice: HubDeviceService,
    private hub: HubDiscoveryService,
    private storage: StorageService,
  ) {}

  /**
   * Idempotent. Never on a device marked as a Payment Terminal (PTM D7:
   * online only), which cold-boots through `/pos/s/<code>`.
   */
  start(): void {
    if (this.sub) return;
    if ((this.storage.getRaw(StorageKey.PAYTERM_DEVICE) ?? '').trim()) {
      // eslint-disable-next-line no-console
      console.info('[hub-bootstrap] Payment Terminal device — hub discovery not started');
      return;
    }
    this.sub = combineLatest([this.storeCtx.activeStore$, this.hubDevice.snapshot$])
      .subscribe(([store, snap]) => this.apply(resolveHubTarget(store, snap)));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }

  private apply(target: HubTarget): void {
    if (target.seed) this.writeSeed();
    if (target.url === this.lastUrl) return;
    this.lastUrl = target.url;
    // eslint-disable-next-line no-console
    console.info('[hub-bootstrap] hub discovery target:', target.url ?? '(none)',
      target.source ? `(from the ${target.source})` : '');
    this.hub.start(target.url);
  }

  /**
   * Write (or drop) the shell's hub on the store, so the router and the
   * interceptor route to the hub discovery probes — both read the active
   * store. Deferred to a microtask because it runs inside an `activeStore$`
   * subscriber, and a synchronous write would re-enter the subject and hand
   * later subscribers the new value before the old one. Re-resolved against
   * the current store and snapshot first, so settings that landed in the
   * meantime win.
   */
  private writeSeed(): void {
    queueMicrotask(() => {
      const seed = resolveHubTarget(this.storeCtx.activeStore, this.hubDevice.snapshot()).seed;
      if (!seed) return;
      if (seed.kind === 'seed') {
        // eslint-disable-next-line no-console
        console.info('[hub-bootstrap] store offline settings not loaded yet — '
          + 'using the hub the edge shell reports for this store:', seed.url);
        this.storeCtx.seedOfflineHubFromShell(seed.url);
      } else {
        // eslint-disable-next-line no-console
        console.info('[hub-bootstrap] the edge shell no longer backs the seeded hub — dropping it');
        this.storeCtx.clearOfflineHubSeed();
      }
    });
  }
}

/**
 * OFP-082 — the APP_INITIALIZER that starts hub discovery, registered in
 * app.config.ts beside the network heartbeat. Browser-only, POS operator
 * routes only, never blocking: it resolves at once, and a failure to start
 * is logged rather than allowed to stop the app from booting.
 */
export const HUB_DISCOVERY_INITIALIZER: Provider = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: () => {
    const platformId = inject(PLATFORM_ID);
    const injector = inject(Injector);
    return () => {
      if (!isPlatformBrowser(platformId) || typeof window === 'undefined') return Promise.resolve();
      if (!hubDiscoveryAppliesTo(injector.get(HUB_BOOT_PATHNAME)())) return Promise.resolve();
      try {
        injector.get(HubBootstrapService).start();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[hub-bootstrap] could not start hub discovery:', err);
      }
      return Promise.resolve();
    };
  },
};
